import { useEffect, useRef, useState } from "react";

import { ChangeNotifier } from "../types/change_notifier";

export abstract class StateMachine<S> extends ChangeNotifier {
  private _state: S;

  constructor(initialState: S) {
    super();
    this._state = initialState;
  }

  get state(): S {
    return this._state;
  }

  setState(newState: S, notify: boolean = true): void {
    this._state = newState;
    if (notify) {
      this.notifyListeners();
    }
  }
}

type StateType<M> = M extends StateMachine<infer S> ? S : never;

export const useStateMachine = <M extends StateMachine<StateType<M>>>(
  machineFactory: () => M,
): [StateType<M>, M] => {
  const ref = useRef<M>(undefined);
  if (!ref.current) {
    ref.current = machineFactory();
  }

  const [state, setState] = useState<StateType<M>>(ref.current.state);

  useEffect(() => {
    const machine = ref.current!;
    const listener = () => {
      setState(machine.state);
    };

    machine.addListener(listener);

    return () => {
      machine.removeListener(listener);
      machine.dispose();
    };
  }, []);

  return [state, ref.current];
};

export const useStateMachineValue = <M extends StateMachine<StateType<M>>>(
  machine: M,
): [StateType<M>, M] => {
  const [state, setState] = useState<StateType<M>>(machine.state);

  useEffect(() => {
    const listener = () => {
      setState(machine.state);
    };

    machine.addListener(listener);
    setState(machine.state);

    return () => {
      machine.removeListener(listener);
    };
  }, [machine]);

  return [state, machine];
};
